import { useState, useEffect } from 'react'
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiLoader, FiAlertCircle } from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'
import { getPlayerStats, getStandings } from '../../api/fixturesApi'

const FIXTURE_STATUSES = ['upcoming', 'live', 'finished']

export default function FixturesManagement({ website, section }) {
  const [activeTab, setActiveTab] = useState('fixtures')
  const [fixtures, setFixtures] = useState([])
  const [standings, setStandings] = useState([])
  const [players, setPlayers] = useState([])
  const [loading, setLoading] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [expandedGoalScorers, setExpandedGoalScorers] = useState(null)
  const [expandedCards, setExpandedCards] = useState(null)
  const [newFixture, setNewFixture] = useState({
    homeTeam: '',
    awayTeam: '',
    matchDate: '',
    matchTimeHour: '12',
    matchTimeMinute: '00',
    matchTimePeriod: 'PM',
    timezone: 'UTC',
    venue: '',
    competition: '',
    status: 'upcoming',
    homeScore: '',
    awayScore: ''
  })
  const [editingFixture, setEditingFixture] = useState(null)
  const [goalScorerForm, setGoalScorerForm] = useState({
    homeTeam: [],
    awayTeam: []
  })
  const [yellowCardForm, setYellowCardForm] = useState({ homeTeam: [], awayTeam: [] })
  const [redCardForm, setRedCardForm] = useState({ homeTeam: [], awayTeam: [] })
  const [newGoalScorer, setNewGoalScorer] = useState({
    team: 'homeTeam',
    playerName: '',
    goalsCount: 1
  })
  const [newCard, setNewCard] = useState({ team: 'homeTeam', playerName: '', cardsCount: 1, color: 'yellow' })
  const [expandedLiveTime, setExpandedLiveTime] = useState(null)
  const [liveTimeForm, setLiveTimeForm] = useState({
    mode: null,
    matchPhase: '1st Half',
    currentMinute: 0,
    stoppageTime: 0,
    isPaused: false
  })
  const [autoTimerInterval, setAutoTimerInterval] = useState(null)

  useEffect(() => {
    fetchFixtures()
    fetchStandings()
    fetchPlayerStats()
  }, [website])

  const fetchFixtures = async () => {
    try {
      setLoading(true)
      const response = await axios.get(`/fixtures/website/${website._id}`)
      setFixtures(response.data.fixtures || [])
      console.log('[Fixtures] Loaded:', response.data.fixtures?.length || 0)
    } catch (error) {
      console.error('[Fixtures] Error loading:', error)
      toast.error('Failed to load fixtures')
    } finally {
      setLoading(false)
    }
  }

  const fetchStandings = async () => {
    try {
      const s = await getStandings(website._id)
      setStandings(s || [])
      console.log('[Standings] Loaded:', (s && s.length) || 0)
    } catch (error) {
      console.error('[Standings] Error loading:', error)
    }
  }

  const fetchPlayerStats = async (teamName = '') => {
    try {
      const p = await getPlayerStats(website._id, teamName)
      setPlayers(p || [])
      console.log('[PlayerStats] Loaded:', (p && p.length) || 0)
    } catch (error) {
      console.error('[PlayerStats] Error loading:', error)
    }
  }

  const resetForm = () => {
    setNewFixture({
      homeTeam: '',
      awayTeam: '',
      matchDate: '',
      matchTimeHour: '12',
      matchTimeMinute: '00',
      matchTimePeriod: 'PM',
      timezone: 'UTC',
      venue: '',
      competition: '',
      status: 'upcoming',
      homeScore: '',
      awayScore: ''
    })
  }

  const validateFixture = (fixture) => {
    if (!fixture.homeTeam?.trim()) {
      toast.error('Home team is required')
      return false
    }
    if (!fixture.awayTeam?.trim()) {
      toast.error('Away team is required')
      return false
    }
    if (fixture.homeTeam.trim().toLowerCase() === fixture.awayTeam.trim().toLowerCase()) {
      toast.error('Home team and away team cannot be the same')
      return false
    }
    if (!fixture.matchDate) {
      toast.error('Match date is required')
      return false
    }
    // Accept either a pre-built `matchTime` string or separate time fields used in the form
    if (!fixture.matchTime && !(fixture.matchTimeHour && fixture.matchTimeMinute && fixture.matchTimePeriod)) {
      toast.error('Match time is required')
      return false
    }
    if (fixture.status === 'finished') {
      if (fixture.homeScore === '' || fixture.homeScore === undefined || fixture.homeScore === null) {
        toast.error('Home score is required for finished matches')
        return false
      }
      if (fixture.awayScore === '' || fixture.awayScore === undefined || fixture.awayScore === null) {
        toast.error('Away score is required for finished matches')
        return false
      }
    }
    return true
  }

  const handleAddFixture = async () => {
    if (!validateFixture(newFixture)) return

    try {
      setLoading(true)
      
      // Convert 12-hour to 24-hour format
      const matchTime24h = convert12to24(newFixture.matchTimeHour, newFixture.matchTimeMinute, newFixture.matchTimePeriod)
      
      const response = await axios.post(`/fixtures/website/${website._id}`, {
        homeTeam: newFixture.homeTeam,
        awayTeam: newFixture.awayTeam,
        matchDate: newFixture.matchDate,
        matchTime: `${newFixture.matchTimeHour.padStart(2, '0')}:${newFixture.matchTimeMinute.padStart(2, '0')} ${newFixture.matchTimePeriod}`,
        timezone: newFixture.timezone,
        venue: newFixture.venue,
        competition: newFixture.competition,
        status: newFixture.status,
        homeScore: (newFixture.status === 'finished' || newFixture.status === 'live') ? (newFixture.homeScore !== '' ? parseInt(newFixture.homeScore) : undefined) : undefined,
        awayScore: (newFixture.status === 'finished' || newFixture.status === 'live') ? (newFixture.awayScore !== '' ? parseInt(newFixture.awayScore) : undefined) : undefined
      })
      // Guard against unexpected responses (avoid inserting undefined into state)
      if (response && response.data && response.data.fixture) {
        setFixtures([...fixtures, response.data.fixture])
      } else {
        // Fallback: reload fixtures from server to keep UI consistent
        await fetchFixtures()
        console.warn('[Fixtures] Unexpected create response, reloaded fixtures')
      }
      resetForm()
      if (['finished', 'live'].includes(newFixture.status)) {
        await fetchStandings()
        await fetchPlayerStats()
      }
      toast.success('Fixture added successfully')
    } catch (error) {
      console.error('[Fixtures] Error adding:', error)
      toast.error(error.response?.data?.message || 'Failed to add fixture')
    } finally {
      setLoading(false)
    }
  }

  const handleRebuildPlayerStats = async () => {
    if (!window.confirm('Rebuild player stats from all finished and live fixtures? This will overwrite current stats.')) return
    try {
      setLoading(true)
      const res = await axios.post(`/fixtures/rebuild-player-stats/${website._id}`)
      toast.success(res.data?.message || 'Player stats rebuilt')
      await fetchPlayerStats()
    } catch (err) {
      console.error('[Rebuild] Error:', err)
      toast.error(err.response?.data?.message || 'Failed to rebuild player stats')
    } finally {
      setLoading(false)
    }
  }

  const handleEditStart = (fixture) => {
    if (!fixture || !fixture._id) {
      toast.error('Invalid fixture data')
      return
    }
    
    try {
      // Safely extract date part - handle both string and Date formats
      let dateString = fixture.matchDate
      if (typeof dateString !== 'string') {
        dateString = new Date(dateString).toISOString()
      }
      const matchDateOnly = dateString.includes('T') ? dateString.split('T')[0] : dateString
      
      // Safely extract time from matchTime field
      let matchTimeValue = fixture.matchTime || '12:00'
      if (fixture.matchTimeUTC && !matchTimeValue) {
        const timeObj = new Date(fixture.matchTimeUTC)
        matchTimeValue = `${String(timeObj.getHours()).padStart(2, '0')}:${String(timeObj.getMinutes()).padStart(2, '0')}`
      }
      
      setEditingId(fixture._id)
      setEditingFixture({
        ...fixture,
        matchDate: matchDateOnly,
        matchTime: matchTimeValue,
        homeScore: fixture.homeScore !== undefined && fixture.homeScore !== null ? fixture.homeScore : '',
        awayScore: fixture.awayScore !== undefined && fixture.awayScore !== null ? fixture.awayScore : '',
        timezone: fixture.timezone || 'UTC'
      })
    } catch (error) {
      console.error('[Fixtures] Error preparing edit:', error)
      toast.error('Failed to load fixture for editing')
    }
  }

  const handleEditSave = async () => {
    if (!validateFixture(editingFixture)) return

    try {
      setLoading(true)
      
      // Prepare payload with proper data types and formatting
      const payload = {
        homeTeam: editingFixture.homeTeam?.trim(),
        awayTeam: editingFixture.awayTeam?.trim(),
        matchDate: editingFixture.matchDate,
        matchTime: editingFixture.matchTime,
        timezone: editingFixture.timezone || 'UTC',
        venue: editingFixture.venue?.trim() || '',
        competition: editingFixture.competition?.trim() || '',
        status: editingFixture.status
      }
      
      // Only include scores for finished or live matches
      if (['finished', 'live'].includes(editingFixture.status)) {
        payload.homeScore = editingFixture.homeScore !== '' ? parseInt(editingFixture.homeScore) : undefined
        payload.awayScore = editingFixture.awayScore !== '' ? parseInt(editingFixture.awayScore) : undefined
      }
      
      console.log('[Fixtures] Saving fixture:', { id: editingId, ...payload })
      const response = await axios.put(`/fixtures/${editingId}`, payload)
      
      if (response.data && response.data.fixture) {
        setFixtures(fixtures.map(f => f._id === editingId ? response.data.fixture : f))
        setEditingId(null)
        setEditingFixture(null)
        await fetchStandings()
        await fetchPlayerStats()
        toast.success('Fixture updated successfully')
      } else {
        throw new Error('Invalid response from server')
      }
    } catch (error) {
      console.error('[Fixtures] Error updating:', error.response?.data || error.message)
      toast.error(error.response?.data?.message || 'Failed to update fixture')
      // Don't close the form on error - allow user to fix and retry
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (fixtureId) => {
    if (!window.confirm('Are you sure you want to delete this fixture?')) return

    try {
      setLoading(true)
      await axios.delete(`/fixtures/${fixtureId}`)
      setFixtures(fixtures.filter(f => f._id !== fixtureId))
      await fetchStandings()
      await fetchPlayerStats()
      toast.success('Fixture deleted successfully')
    } catch (error) {
      console.error('[Fixtures] Error deleting:', error)
      toast.error(error.response?.data?.message || 'Failed to delete fixture')
    } finally {
      setLoading(false)
    }
  }

  const handleAddGoalScorer = () => {
    if (!newGoalScorer.playerName.trim()) {
      toast.error('Player name is required')
      return
    }

    const team = newGoalScorer.team
    setGoalScorerForm({
      ...goalScorerForm,
      [team]: [
        ...goalScorerForm[team],
        {
          playerName: newGoalScorer.playerName.trim(),
          goalsCount: parseInt(newGoalScorer.goalsCount) || 1
        }
      ]
    })

    setNewGoalScorer({ team: 'homeTeam', playerName: '', goalsCount: 1 })
    toast.success('Goal scorer added')
  }

  const handleRemoveGoalScorer = (team, index) => {
    setGoalScorerForm({
      ...goalScorerForm,
      [team]: goalScorerForm[team].filter((_, i) => i !== index)
    })
  }

  const calculateTotalGoals = (scorers) => {
    return scorers.reduce((total, scorer) => total + (scorer.goalsCount || 0), 0)
  }

  const handleSaveGoalScorers = async (fixture) => {
    try {
      // Validate goal scorer totals
      const homeTotal = calculateTotalGoals(goalScorerForm.homeTeam)
      const awayTotal = calculateTotalGoals(goalScorerForm.awayTeam)

      // Only strictly validate totals for finished matches
      if (fixture.status === 'finished') {
        if (homeTotal !== fixture.homeScore) {
          toast.error(`Home goal scorers total (${homeTotal}) doesn't match home score (${fixture.homeScore})`)
          return
        }

        if (awayTotal !== fixture.awayScore) {
          toast.error(`Away goal scorers total (${awayTotal}) doesn't match away score (${fixture.awayScore})`)
          return
        }
      }

      setLoading(true)
      const response = await axios.post(`/fixtures/${fixture._id}/goal-scorers`, {
        homeGoalScorers: goalScorerForm.homeTeam,
        awayGoalScorers: goalScorerForm.awayTeam
      })

      setFixtures(fixtures.map(f => f._id === fixture._id ? response.data.fixture : f))
      setExpandedGoalScorers(null)
      setGoalScorerForm({ homeTeam: [], awayTeam: [] })
      setYellowCardForm({ homeTeam: [], awayTeam: [] })
      setRedCardForm({ homeTeam: [], awayTeam: [] })
      setNewCard({ team: 'homeTeam', playerName: '', cardsCount: 1, color: 'yellow' })
      await fetchStandings()
      await fetchPlayerStats()
      toast.success('Goal scorers saved successfully')
    } catch (error) {
      console.error('[Fixtures] Error saving goal scorers:', error)
      toast.error(error.response?.data?.message || 'Failed to save goal scorers')
    } finally {
      setLoading(false)
    }
  }

  const handleEditGoalScorers = (fixture) => {
    setExpandedGoalScorers(fixture._id)
    setGoalScorerForm({
      homeTeam: fixture.goalScorers?.homeTeam || [],
      awayTeam: fixture.goalScorers?.awayTeam || []
    })
  }

  const handleEditCards = (fixture) => {
    setExpandedCards(fixture._id)
    setYellowCardForm({
      homeTeam: fixture.yellowCards?.homeTeam || [],
      awayTeam: fixture.yellowCards?.awayTeam || []
    })
    setRedCardForm({
      homeTeam: fixture.redCards?.homeTeam || [],
      awayTeam: fixture.redCards?.awayTeam || []
    })
  }

  const handleSaveCards = async (fixture) => {
    try {
      setLoading(true)
      const response = await axios.post(`/fixtures/${fixture._id}/cards`, {
        homeYellowCards: yellowCardForm.homeTeam,
        awayYellowCards: yellowCardForm.awayTeam,
        homeRedCards: redCardForm.homeTeam,
        awayRedCards: redCardForm.awayTeam
      })
      setFixtures(fixtures.map(f => f._id === fixture._id ? response.data.fixture : f))
      setExpandedCards(null)
      setYellowCardForm({ homeTeam: [], awayTeam: [] })
      setRedCardForm({ homeTeam: [], awayTeam: [] })
      await fetchStandings()
      await fetchPlayerStats()
      toast.success('Cards saved successfully')
    } catch (error) {
      console.error('[Fixtures] Error saving cards:', error)
      toast.error(error.response?.data?.message || 'Failed to save cards')
    } finally {
      setLoading(false)
    }
  }

  const handleAddCard = (color) => {
    if (!newCard.playerName.trim()) {
      toast.error('Player name is required')
      return
    }
    const team = newCard.team
    const entry = { playerName: newCard.playerName.trim(), cardsCount: parseInt(newCard.cardsCount) || 1 }
    if (color === 'yellow') {
      setYellowCardForm({ ...yellowCardForm, [team]: [...yellowCardForm[team], entry] })
    } else {
      setRedCardForm({ ...redCardForm, [team]: [...redCardForm[team], entry] })
    }
    setNewCard({ team: newCard.team, playerName: '', cardsCount: 1, color })
    toast.success(`${color === 'yellow' ? 'Yellow' : 'Red'} card added`)
  }

  const handleRemoveCard = (color, team, index) => {
    if (color === 'yellow') {
      setYellowCardForm({ ...yellowCardForm, [team]: yellowCardForm[team].filter((_, i) => i !== index) })
    } else {
      setRedCardForm({ ...redCardForm, [team]: redCardForm[team].filter((_, i) => i !== index) })
    }
  }

  const handleEditLiveTime = async (fixture) => {
    setExpandedLiveTime(fixture._id)

    if (fixture.liveMatchTime?.mode === 'auto') {
      try {
        setLoading(true)
        const response = await axios.get(`/fixtures/${fixture._id}/auto-detect-state`)
        const matchState = response.data.matchState

        setLiveTimeForm({
          mode: 'auto',
          matchPhase: matchState.matchPhase || '1st Half',
          currentMinute: matchState.currentMinute || 0,
          stoppageTime: matchState.stoppageTime || 0,
          isPaused: !!matchState.isPaused
        })
      } catch (error) {
        console.error('[Live Time] Error fetching auto state:', error)
        toast.error('Failed to fetch auto match state')
      } finally {
        setLoading(false)
      }
    } else {
      setLiveTimeForm({
        mode: fixture.liveMatchTime?.mode || null,
        matchPhase: fixture.liveMatchTime?.matchPhase || '1st Half',
        currentMinute: fixture.liveMatchTime?.currentMinute || 0,
        stoppageTime: fixture.liveMatchTime?.stoppageTime || 0,
        isPaused: fixture.liveMatchTime?.isPaused || false
      })
    }
  }

  const handleSaveLiveTime = async (fixture) => {
    try {
      setLoading(true)
      const response = await axios.post(`/fixtures/${fixture._id}/live-time`, {
        mode: liveTimeForm.mode,
        matchPhase: liveTimeForm.matchPhase,
        currentMinute: parseInt(liveTimeForm.currentMinute) || 0,
        stoppageTime: parseInt(liveTimeForm.stoppageTime) || 0,
        isPaused: liveTimeForm.isPaused
      })
      setFixtures(fixtures.map(f => f._id === fixture._id ? response.data.fixture : f))
      setExpandedLiveTime(null)
      toast.success('Live match time updated successfully')
    } catch (error) {
      console.error('[Live Time] Error saving:', error)
      toast.error(error.response?.data?.message || 'Failed to update live match time')
    } finally {
      setLoading(false)
    }
  }

  const handleStartAutoTime = async (fixture) => {
    try {
      setLoading(true)
      const response = await axios.post(`/fixtures/${fixture._id}/auto-detect/start`)
      setFixtures(fixtures.map(f => f._id === fixture._id ? response.data.fixture : f))
      setLiveTimeForm({
        mode: 'auto',
        matchPhase: '1st Half',
        currentMinute: 0,
        stoppageTime: 0,
        isPaused: false
      })
      toast.success('Auto-detection started')
    } catch (error) {
      console.error('[Live Time] Error starting auto:', error)
      toast.error(error.response?.data?.message || 'Failed to start auto-detection')
    } finally {
      setLoading(false)
    }
  }

  const handleTogglePause = async (fixture) => {
    try {
      setLoading(true)
      const currentLiveTime = fixture.liveMatchTime
      const route = currentLiveTime?.isPaused ? 'resume' : 'pause'
      const response = await axios.post(`/fixtures/${fixture._id}/auto-detect/${route}`)
      setFixtures(fixtures.map(f => f._id === fixture._id ? response.data.fixture : f))

      if (response.data.matchState) {
        setLiveTimeForm({
          ...liveTimeForm,
          matchPhase: response.data.matchState.matchPhase,
          currentMinute: response.data.matchState.currentMinute,
          stoppageTime: response.data.matchState.stoppageTime,
          isPaused: response.data.matchState.isPaused
        })
      } else {
        setLiveTimeForm({...liveTimeForm, isPaused: !liveTimeForm.isPaused})
      }

      toast.success(currentLiveTime?.isPaused ? 'Timer resumed' : 'Timer paused')
    } catch (error) {
      console.error('[Live Time] Error toggling pause:', error)
      toast.error(error.response?.data?.message || 'Failed to toggle pause')
    } finally {
      setLoading(false)
    }
  }

  const handleStartSecondHalf = async (fixture) => {
    try {
      setLoading(true)
      const response = await axios.post(`/fixtures/${fixture._id}/auto-detect/next-half`)
      setFixtures(fixtures.map(f => f._id === fixture._id ? response.data.fixture : f))

      if (response.data.matchState) {
        setLiveTimeForm({
          ...liveTimeForm,
          matchPhase: response.data.matchState.matchPhase,
          currentMinute: response.data.matchState.currentMinute,
          stoppageTime: response.data.matchState.stoppageTime,
          isPaused: response.data.matchState.isPaused
        })
      }

      toast.success('Second half started')
    } catch (error) {
      console.error('[Live Time] Error starting 2nd half:', error)
      toast.error(error.response?.data?.message || 'Failed to start second half')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString()
    } catch {
      return dateString
    }
  }

  /**
   * Get user's browser timezone
   */
  const getUserTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  }

  /**
   * Convert 12-hour format (HH:MM with AM/PM) to 24-hour format
   */
  const convert12to24 = (hour, minute, period) => {
    let h = parseInt(hour)
    if (period === 'AM') {
      if (h === 12) h = 0
    } else if (period === 'PM') {
      if (h !== 12) h += 12
    }
    return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
  }

  /**
   * Convert UTC time to local timezone and format as 12-hour
   */
  const normalizeTimezone = (timezone) => {
    if (!timezone || typeof timezone !== 'string') return 'UTC'

    const aliasMap = {
      'Europe/Lagos': 'Africa/Lagos'
    }

    return aliasMap[timezone] || timezone
  }

  const convertUTCToLocal = (utcDate, timezone) => {
    const tz = normalizeTimezone(timezone)

    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
      
      const parts = formatter.formatToParts(new Date(utcDate))
      const hour = parts.find(p => p.type === 'hour')?.value || '00'
      const minute = parts.find(p => p.type === 'minute')?.value || '00'
      
      let h = parseInt(hour)
      const period = h >= 12 ? 'PM' : 'AM'
      const h12 = h % 12 || 12
      
      return {
        hour: String(h12).padStart(2, '0'),
        minute,
        period,
        time12h: `${String(h12).padStart(2, '0')}:${minute} ${period}`
      }
    } catch (error) {
      console.error('[FixturesManagement] Invalid timezone:', timezone, error)

      const fallbackFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'UTC',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
      const time12h = fallbackFormatter.format(new Date(utcDate))
      return {
        hour: time12h.split(':')[0],
        minute: time12h.split(':')[1].slice(0, 2),
        period: time12h.includes('PM') ? 'PM' : 'AM',
        time12h
      }
    }
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-0">
        <button
          type="button"
          onClick={() => setActiveTab('fixtures')}
          className={`px-4 py-3 font-medium transition-colors border-b-2 ${
            activeTab === 'fixtures'
              ? 'text-primary-600 border-primary-600'
              : 'text-gray-600 hover:text-gray-900 border-transparent'
          }`}
        >
          📅 Fixtures Management
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('standings')}
          className={`px-4 py-3 font-medium transition-colors border-b-2 ${
            activeTab === 'standings'
              ? 'text-primary-600 border-primary-600'
              : 'text-gray-600 hover:text-gray-900 border-transparent'
          }`}
        >
          🏆 League Table
        </button>
      </div>

      {/* Fixtures Tab */}
      {activeTab === 'fixtures' && (
        <>
      {/* Add Fixture Form */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">➕ Add New Fixture</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <input
            type="text"
            placeholder="Home Team"
            value={newFixture.homeTeam}
            onChange={(e) => setNewFixture({ ...newFixture, homeTeam: e.target.value })}
            className="input-field w-full"
          />
          <input
            type="text"
            placeholder="Away Team"
            value={newFixture.awayTeam}
            onChange={(e) => setNewFixture({ ...newFixture, awayTeam: e.target.value })}
            className="input-field w-full"
          />
          <input
            type="date"
            value={newFixture.matchDate}
            onChange={(e) => setNewFixture({ ...newFixture, matchDate: e.target.value })}
            className="input-field w-full"
          />

          {/* Time in 12-hour format */}
          <div className="grid grid-cols-3 gap-2">
            <input
              type="number"
              placeholder="Hour"
              value={newFixture.matchTimeHour}
              onChange={(e) => setNewFixture({ ...newFixture, matchTimeHour: e.target.value })}
              min="1"
              max="12"
              className="input-field w-full text-center"
            />
            <input
              type="number"
              placeholder="Min"
              value={newFixture.matchTimeMinute}
              onChange={(e) => setNewFixture({ ...newFixture, matchTimeMinute: e.target.value })}
              min="0"
              max="59"
              className="input-field w-full text-center"
            />
            <select
              value={newFixture.matchTimePeriod}
              onChange={(e) => setNewFixture({ ...newFixture, matchTimePeriod: e.target.value })}
              className="input-field w-full text-center"
            >
              <option value="AM">AM</option>
              <option value="PM">PM</option>
            </select>
          </div>

          {/* Timezone Selector */}
          <select
            value={newFixture.timezone}
            onChange={(e) => setNewFixture({ ...newFixture, timezone: e.target.value })}
            className="input-field w-full"
          >
            <option value="UTC">UTC</option>
            <option value="America/New_York">Eastern Time (ET)</option>
            <option value="America/Chicago">Central Time (CT)</option>
            <option value="America/Denver">Mountain Time (MT)</option>
            <option value="America/Los_Angeles">Pacific Time (PT)</option>
            <option value="Europe/London">GMT (London)</option>
            <option value="Europe/Paris">CET (Paris)</option>
            <option value="Africa/Lagos">WAT (Lagos)</option>
            <option value="Asia/Dubai">GST (Dubai)</option>
            <option value="Asia/Kolkata">IST (India)</option>
            <option value="Asia/Bangkok">ICT (Bangkok)</option>
            <option value="Asia/Singapore">SGT (Singapore)</option>
            <option value="Asia/Tokyo">JST (Japan)</option>
            <option value="Australia/Sydney">AEDT (Sydney)</option>
            <option value="Pacific/Auckland">NZDT (Auckland)</option>
          </select>
          <input
            type="text"
            placeholder="Venue/Stadium (optional)"
            value={newFixture.venue}
            onChange={(e) => setNewFixture({ ...newFixture, venue: e.target.value })}
            className="input-field w-full"
          />
          <input
            type="text"
            placeholder="Competition/League (optional)"
            value={newFixture.competition}
            onChange={(e) => setNewFixture({ ...newFixture, competition: e.target.value })}
            className="input-field w-full"
          />
          
          <select
            value={newFixture.status}
            onChange={(e) => setNewFixture({ ...newFixture, status: e.target.value })}
            className="input-field w-full"
          >
            {FIXTURE_STATUSES.map(status => (
              <option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>

          {['finished', 'live'].includes(newFixture.status) && (
            <>
              <input
                type="number"
                placeholder="Home Score"
                value={newFixture.homeScore}
                onChange={(e) => setNewFixture({ ...newFixture, homeScore: e.target.value })}
                min="0"
                className="input-field w-full"
              />
              <input
                type="number"
                placeholder="Away Score"
                value={newFixture.awayScore}
                onChange={(e) => setNewFixture({ ...newFixture, awayScore: e.target.value })}
                min="0"
                className="input-field w-full"
              />
            </>
          )}
        </div>

        <button
          type="button"
          onClick={handleAddFixture}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors"
        >
          {loading ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiPlus className="w-4 h-4" />}
          Add Fixture
        </button>
        <button
          type="button"
          onClick={handleRebuildPlayerStats}
          disabled={loading}
          className="ml-3 inline-flex items-center gap-2 px-3 py-2 bg-gray-800 text-white hover:bg-gray-900 disabled:bg-gray-400 rounded-lg font-medium transition-colors"
        >
          🔁 Rebuild Top Scorers
        </button>
      </div>

      {/* Fixtures Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Home Team</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Away Team</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Time</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Venue</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Score</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {fixtures.length === 0 && !loading && (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    No fixtures yet. Add one above to get started!
                  </td>
                </tr>
              )}

              {fixtures.filter(Boolean).map((fixture) => (
                <tr key={fixture._id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                  {editingId === fixture._id ? (
                    <>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={editingFixture.homeTeam}
                          onChange={(e) => setEditingFixture({ ...editingFixture, homeTeam: e.target.value })}
                          className="input-field w-full text-sm"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={editingFixture.awayTeam}
                          onChange={(e) => setEditingFixture({ ...editingFixture, awayTeam: e.target.value })}
                          className="input-field w-full text-sm"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="date"
                          value={editingFixture.matchDate}
                          onChange={(e) => setEditingFixture({ ...editingFixture, matchDate: e.target.value })}
                          className="input-field w-full text-sm"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="time"
                          value={editingFixture.matchTime}
                          onChange={(e) => setEditingFixture({ ...editingFixture, matchTime: e.target.value })}
                          className="input-field w-full text-sm"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={editingFixture.venue}
                          onChange={(e) => setEditingFixture({ ...editingFixture, venue: e.target.value })}
                          className="input-field w-full text-sm"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={editingFixture.status}
                          onChange={(e) => setEditingFixture({ ...editingFixture, status: e.target.value })}
                          className="input-field w-full text-sm"
                        >
                          {FIXTURE_STATUSES.map(status => (
                            <option key={status} value={status}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        {['finished', 'live'].includes(editingFixture.status) ? (
                          <div className="flex gap-1">
                            <input
                              type="number"
                              value={editingFixture.homeScore}
                              onChange={(e) => setEditingFixture({ ...editingFixture, homeScore: e.target.value })}
                              min="0"
                              className="input-field w-16 text-sm text-center"
                            />
                            <span className="text-sm font-semibold">-</span>
                            <input
                              type="number"
                              value={editingFixture.awayScore}
                              onChange={(e) => setEditingFixture({ ...editingFixture, awayScore: e.target.value })}
                              min="0"
                              className="input-field w-16 text-sm text-center"
                            />
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex gap-2 justify-center">
                          <button
                            type="button"
                            onClick={handleEditSave}
                            disabled={loading}
                            className="p-1 text-green-600 hover:text-green-900 disabled:opacity-50"
                          >
                            <FiSave className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingId(null)
                              setEditingFixture(null)
                            }}
                            className="p-1 text-gray-600 hover:text-gray-900"
                          >
                            <FiX className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{fixture.homeTeam}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{fixture.awayTeam}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatDate(fixture.matchDate)}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {fixture.matchTimeUTC ? convertUTCToLocal(new Date(fixture.matchTimeUTC), fixture.timezone).time12h : fixture.matchTime}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{fixture.venue || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                          fixture.status === 'finished' ? 'bg-green-100 text-green-800' :
                          fixture.status === 'live' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {fixture.status.charAt(0).toUpperCase() + fixture.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                        {['finished', 'live'].includes(fixture.status) ? (
                          <span>{fixture.homeScore ?? '-'} - {fixture.awayScore ?? '-'}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex gap-2 justify-center">
                          {['finished', 'live'].includes(fixture.status) && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleEditGoalScorers(fixture)}
                                className="px-2 py-1 text-xs bg-purple-600 text-white hover:bg-purple-700 rounded"
                                title="Edit Goal Scorers"
                              >
                                Goals
                              </button>
                              <button
                                type="button"
                                onClick={() => handleEditCards(fixture)}
                                className="px-2 py-1 text-xs bg-yellow-600 text-white hover:bg-yellow-700 rounded"
                                title="Edit Cards"
                              >
                                Cards
                              </button>
                            </>
                          )}
                          {fixture.status === 'live' && (
                            <button
                              type="button"
                              onClick={() => handleEditLiveTime(fixture)}
                              className="px-2 py-1 text-xs bg-red-600 text-white hover:bg-red-700 rounded"
                              title="Live Match Time"
                            >
                              ⏱️ Time
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleEditStart(fixture)}
                            className="p-1 text-blue-600 hover:text-blue-900"
                          >
                            <FiEdit2 className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(fixture._id)}
                            className="p-1 text-red-600 hover:text-red-900"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
        </>
      )}

      {/* Goal Scorers Editor */}
      {expandedGoalScorers && (
        <div className="bg-white border border-purple-300 rounded-lg p-4 mt-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Goal Scorers</h3>
          
          {fixtures.find(f => f._id === expandedGoalScorers) && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Home Team Goal Scorers */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">
                    {fixtures.find(f => f._id === expandedGoalScorers)?.homeTeam} Goal Scorers
                    <span className="ml-2 text-sm text-gray-600">
                      (Total: {calculateTotalGoals(goalScorerForm.homeTeam)} / {fixtures.find(f => f._id === expandedGoalScorers)?.homeScore})
                    </span>
                  </h4>
                  
                  <div className="space-y-2 mb-4">
                    {goalScorerForm.homeTeam.map((scorer, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm">
                          <span className="font-medium">{scorer.playerName}</span>
                          <span className="text-gray-600 ml-2">({scorer.goalsCount})</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveGoalScorer('homeTeam', index)}
                          className="p-1 text-red-600 hover:text-red-900"
                          title="Remove scorer"
                        >
                          <FiX className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {newGoalScorer.team === 'homeTeam' && (
                    <div className="space-y-2 p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3">
                      <input
                        type="text"
                        placeholder="Player name"
                        value={newGoalScorer.playerName}
                        onChange={(e) => setNewGoalScorer({ ...newGoalScorer, playerName: e.target.value })}
                        className="input-field w-full text-sm"
                      />
                      <input
                        type="number"
                        placeholder="Number of goals"
                        value={newGoalScorer.goalsCount}
                        onChange={(e) => setNewGoalScorer({ ...newGoalScorer, goalsCount: e.target.value })}
                        min="1"
                        className="input-field w-full text-sm"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleAddGoalScorer}
                          className="flex-1 px-2 py-1 bg-blue-600 text-white text-sm hover:bg-blue-700 rounded"
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewGoalScorer({ team: 'homeTeam', playerName: '', goalsCount: 1 })}
                          className="px-2 py-1 bg-gray-300 text-gray-900 text-sm hover:bg-gray-400 rounded"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  )}

                  {newGoalScorer.team !== 'homeTeam' && (
                    <button
                      type="button"
                      onClick={() => setNewGoalScorer({ ...newGoalScorer, team: 'homeTeam', playerName: '', goalsCount: 1 })}
                      className="w-full px-3 py-2 border border-blue-300 text-blue-600 hover:bg-blue-50 text-sm rounded-lg font-medium transition-colors"
                    >
                      + Add Goal Scorer
                    </button>
                  )}
                </div>

                {/* Away Team Goal Scorers */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">
                    {fixtures.find(f => f._id === expandedGoalScorers)?.awayTeam} Goal Scorers
                    <span className="ml-2 text-sm text-gray-600">
                      (Total: {calculateTotalGoals(goalScorerForm.awayTeam)} / {fixtures.find(f => f._id === expandedGoalScorers)?.awayScore})
                    </span>
                  </h4>
                  
                  <div className="space-y-2 mb-4">
                    {goalScorerForm.awayTeam.map((scorer, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm">
                          <span className="font-medium">{scorer.playerName}</span>
                          <span className="text-gray-600 ml-2">({scorer.goalsCount})</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveGoalScorer('awayTeam', index)}
                          className="p-1 text-red-600 hover:text-red-900"
                          title="Remove scorer"
                        >
                          <FiX className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {newGoalScorer.team === 'awayTeam' && (
                    <div className="space-y-2 p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3">
                      <input
                        type="text"
                        placeholder="Player name"
                        value={newGoalScorer.playerName}
                        onChange={(e) => setNewGoalScorer({ ...newGoalScorer, playerName: e.target.value })}
                        className="input-field w-full text-sm"
                      />
                      <input
                        type="number"
                        placeholder="Number of goals"
                        value={newGoalScorer.goalsCount}
                        onChange={(e) => setNewGoalScorer({ ...newGoalScorer, goalsCount: e.target.value })}
                        min="1"
                        className="input-field w-full text-sm"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleAddGoalScorer}
                          className="flex-1 px-2 py-1 bg-blue-600 text-white text-sm hover:bg-blue-700 rounded"
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewGoalScorer({ team: 'awayTeam', playerName: '', goalsCount: 1 })}
                          className="px-2 py-1 bg-gray-300 text-gray-900 text-sm hover:bg-gray-400 rounded"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  )}

                  {newGoalScorer.team !== 'awayTeam' && (
                    <button
                      type="button"
                      onClick={() => setNewGoalScorer({ ...newGoalScorer, team: 'awayTeam', playerName: '', goalsCount: 1 })}
                      className="w-full px-3 py-2 border border-blue-300 text-blue-600 hover:bg-blue-50 text-sm rounded-lg font-medium transition-colors"
                    >
                      + Add Goal Scorer
                    </button>
                  )}
                </div>
              </div>

              {/* Validation Warning */}
              {(() => {
                const fixture = fixtures.find(f => f._id === expandedGoalScorers)
                const homeTotal = calculateTotalGoals(goalScorerForm.homeTeam)
                const awayTotal = calculateTotalGoals(goalScorerForm.awayTeam)
                const homeMatches = homeTotal === fixture.homeScore
                const awayMatches = awayTotal === fixture.awayScore

                if (!homeMatches || !awayMatches) {
                  return (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg flex gap-2">
                      <FiAlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-yellow-800">
                        <p className="font-semibold mb-1">Goal Scorer Totals Don't Match Scores:</p>
                        {!homeMatches && (
                          <p>{fixture.homeTeam}: {homeTotal} goals vs {fixture.homeScore} score</p>
                        )}
                        {!awayMatches && (
                          <p>{fixture.awayTeam}: {awayTotal} goals vs {fixture.awayScore} score</p>
                        )}
                      </div>
                    </div>
                  )
                }
                return null
              })()}

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setExpandedGoalScorers(null)
                    setGoalScorerForm({ homeTeam: [], awayTeam: [] })
                    setNewGoalScorer({ team: 'homeTeam', playerName: '', goalsCount: 1 })
                    setYellowCardForm({ homeTeam: [], awayTeam: [] })
                    setRedCardForm({ homeTeam: [], awayTeam: [] })
                    setNewCard({ team: 'homeTeam', playerName: '', cardsCount: 1, color: 'yellow' })
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleSaveGoalScorers(fixtures.find(f => f._id === expandedGoalScorers))}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  {loading ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiSave className="w-4 h-4" />}
                  Save Goal Scorers
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Cards Editor */}
      {expandedCards && (
        <div className="bg-white border border-yellow-300 rounded-lg p-4 mt-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Cards (Yellow/Red)</h3>
          
          {fixtures.find(f => f._id === expandedCards) && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Home Team Cards */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">{fixtures.find(f => f._id === expandedCards)?.homeTeam} Cards</h4>
                  
                  {/* Yellow Cards */}
                  <div className="mb-4">
                    <p className="text-xs font-medium text-yellow-700 mb-2">Yellow Cards</p>
                    <div className="space-y-2 mb-2">
                      {yellowCardForm.homeTeam.map((c, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-yellow-50 p-2 rounded">
                          <span className="text-sm"><span className="font-medium">{c.playerName}</span> <span className="text-gray-600 ml-1">({c.cardsCount})</span></span>
                          <button type="button" onClick={() => handleRemoveCard('yellow', 'homeTeam', idx)} className="p-1 text-red-600 hover:text-red-900"><FiX className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>
                    {newCard.team === 'homeTeam' && newCard.color === 'yellow' && (
                      <div className="space-y-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg mb-2">
                        <input type="text" placeholder="Player name" value={newCard.playerName} onChange={(e) => setNewCard({ ...newCard, playerName: e.target.value })} className="input-field w-full text-sm" />
                        <input type="number" placeholder="Cards" value={newCard.cardsCount} onChange={(e) => setNewCard({ ...newCard, cardsCount: e.target.value })} min="1" className="input-field w-full text-sm" />
                        <div className="flex gap-2">
                          <button type="button" onClick={() => handleAddCard('yellow')} className="flex-1 px-2 py-1 bg-yellow-600 text-white text-xs hover:bg-yellow-700 rounded">Add</button>
                          <button type="button" onClick={() => setNewCard({ team: 'homeTeam', playerName: '', cardsCount: 1, color: 'yellow' })} className="px-2 py-1 bg-gray-300 text-gray-900 text-xs hover:bg-gray-400 rounded">Clear</button>
                        </div>
                      </div>
                    )}
                    {!(newCard.team === 'homeTeam' && newCard.color === 'yellow') && (
                      <button type="button" onClick={() => setNewCard({ team: 'homeTeam', playerName: '', cardsCount: 1, color: 'yellow' })} className="w-full px-2 py-1 border border-yellow-300 text-yellow-700 hover:bg-yellow-50 text-xs rounded font-medium">+ Add Yellow</button>
                    )}
                  </div>

                  {/* Red Cards */}
                  <div>
                    <p className="text-xs font-medium text-red-700 mb-2">Red Cards</p>
                    <div className="space-y-2 mb-2">
                      {redCardForm.homeTeam.map((c, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-red-50 p-2 rounded">
                          <span className="text-sm"><span className="font-medium">{c.playerName}</span> <span className="text-gray-600 ml-1">({c.cardsCount})</span></span>
                          <button type="button" onClick={() => handleRemoveCard('red', 'homeTeam', idx)} className="p-1 text-red-600 hover:text-red-900"><FiX className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>
                    {newCard.team === 'homeTeam' && newCard.color === 'red' && (
                      <div className="space-y-2 p-2 bg-red-50 border border-red-200 rounded-lg mb-2">
                        <input type="text" placeholder="Player name" value={newCard.playerName} onChange={(e) => setNewCard({ ...newCard, playerName: e.target.value })} className="input-field w-full text-sm" />
                        <input type="number" placeholder="Cards" value={newCard.cardsCount} onChange={(e) => setNewCard({ ...newCard, cardsCount: e.target.value })} min="1" className="input-field w-full text-sm" />
                        <div className="flex gap-2">
                          <button type="button" onClick={() => handleAddCard('red')} className="flex-1 px-2 py-1 bg-red-600 text-white text-xs hover:bg-red-700 rounded">Add</button>
                          <button type="button" onClick={() => setNewCard({ team: 'homeTeam', playerName: '', cardsCount: 1, color: 'red' })} className="px-2 py-1 bg-gray-300 text-gray-900 text-xs hover:bg-gray-400 rounded">Clear</button>
                        </div>
                      </div>
                    )}
                    {!(newCard.team === 'homeTeam' && newCard.color === 'red') && (
                      <button type="button" onClick={() => setNewCard({ team: 'homeTeam', playerName: '', cardsCount: 1, color: 'red' })} className="w-full px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 text-xs rounded font-medium">+ Add Red</button>
                    )}
                  </div>
                </div>

                {/* Away Team Cards */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">{fixtures.find(f => f._id === expandedCards)?.awayTeam} Cards</h4>
                  
                  {/* Yellow Cards */}
                  <div className="mb-4">
                    <p className="text-xs font-medium text-yellow-700 mb-2">Yellow Cards</p>
                    <div className="space-y-2 mb-2">
                      {yellowCardForm.awayTeam.map((c, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-yellow-50 p-2 rounded">
                          <span className="text-sm"><span className="font-medium">{c.playerName}</span> <span className="text-gray-600 ml-1">({c.cardsCount})</span></span>
                          <button type="button" onClick={() => handleRemoveCard('yellow', 'awayTeam', idx)} className="p-1 text-red-600 hover:text-red-900"><FiX className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>
                    {newCard.team === 'awayTeam' && newCard.color === 'yellow' && (
                      <div className="space-y-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg mb-2">
                        <input type="text" placeholder="Player name" value={newCard.playerName} onChange={(e) => setNewCard({ ...newCard, playerName: e.target.value })} className="input-field w-full text-sm" />
                        <input type="number" placeholder="Cards" value={newCard.cardsCount} onChange={(e) => setNewCard({ ...newCard, cardsCount: e.target.value })} min="1" className="input-field w-full text-sm" />
                        <div className="flex gap-2">
                          <button type="button" onClick={() => handleAddCard('yellow')} className="flex-1 px-2 py-1 bg-yellow-600 text-white text-xs hover:bg-yellow-700 rounded">Add</button>
                          <button type="button" onClick={() => setNewCard({ team: 'awayTeam', playerName: '', cardsCount: 1, color: 'yellow' })} className="px-2 py-1 bg-gray-300 text-gray-900 text-xs hover:bg-gray-400 rounded">Clear</button>
                        </div>
                      </div>
                    )}
                    {!(newCard.team === 'awayTeam' && newCard.color === 'yellow') && (
                      <button type="button" onClick={() => setNewCard({ team: 'awayTeam', playerName: '', cardsCount: 1, color: 'yellow' })} className="w-full px-2 py-1 border border-yellow-300 text-yellow-700 hover:bg-yellow-50 text-xs rounded font-medium">+ Add Yellow</button>
                    )}
                  </div>

                  {/* Red Cards */}
                  <div>
                    <p className="text-xs font-medium text-red-700 mb-2">Red Cards</p>
                    <div className="space-y-2 mb-2">
                      {redCardForm.awayTeam.map((c, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-red-50 p-2 rounded">
                          <span className="text-sm"><span className="font-medium">{c.playerName}</span> <span className="text-gray-600 ml-1">({c.cardsCount})</span></span>
                          <button type="button" onClick={() => handleRemoveCard('red', 'awayTeam', idx)} className="p-1 text-red-600 hover:text-red-900"><FiX className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>
                    {newCard.team === 'awayTeam' && newCard.color === 'red' && (
                      <div className="space-y-2 p-2 bg-red-50 border border-red-200 rounded-lg mb-2">
                        <input type="text" placeholder="Player name" value={newCard.playerName} onChange={(e) => setNewCard({ ...newCard, playerName: e.target.value })} className="input-field w-full text-sm" />
                        <input type="number" placeholder="Cards" value={newCard.cardsCount} onChange={(e) => setNewCard({ ...newCard, cardsCount: e.target.value })} min="1" className="input-field w-full text-sm" />
                        <div className="flex gap-2">
                          <button type="button" onClick={() => handleAddCard('red')} className="flex-1 px-2 py-1 bg-red-600 text-white text-xs hover:bg-red-700 rounded">Add</button>
                          <button type="button" onClick={() => setNewCard({ team: 'awayTeam', playerName: '', cardsCount: 1, color: 'red' })} className="px-2 py-1 bg-gray-300 text-gray-900 text-xs hover:bg-gray-400 rounded">Clear</button>
                        </div>
                      </div>
                    )}
                    {!(newCard.team === 'awayTeam' && newCard.color === 'red') && (
                      <button type="button" onClick={() => setNewCard({ team: 'awayTeam', playerName: '', cardsCount: 1, color: 'red' })} className="w-full px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 text-xs rounded font-medium">+ Add Red</button>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setExpandedCards(null)
                    setYellowCardForm({ homeTeam: [], awayTeam: [] })
                    setRedCardForm({ homeTeam: [], awayTeam: [] })
                    setNewCard({ team: 'homeTeam', playerName: '', cardsCount: 1, color: 'yellow' })
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleSaveCards(fixtures.find(f => f._id === expandedCards))}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  {loading ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiSave className="w-4 h-4" />}
                  Save Cards
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Live Time Editor Modal */}
      {expandedLiveTime && (
        <div className="bg-white border border-red-300 rounded-lg p-4 mt-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">⏱️ Live Match Time Control</h3>
          
          {fixtures.find(f => f._id === expandedLiveTime) && (
            <>
              {/* Current Status Display */}
              {liveTimeForm.mode && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-gray-900 font-medium">
                    Current: <span className="text-lg font-bold text-blue-600">{liveTimeForm.currentMinute}'</span>
                    <span className="ml-2 text-gray-600">{liveTimeForm.matchPhase}</span>
                    {liveTimeForm.stoppageTime > 0 && <span className="ml-2 text-orange-600">+{liveTimeForm.stoppageTime} min</span>}
                    {liveTimeForm.isPaused && <span className="ml-2 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full font-medium">PAUSED</span>}
                  </p>
                </div>
              )}

              {/* Mode Selection */}
              <div className="mb-6">
                <p className="text-sm font-medium text-gray-900 mb-3">Select Mode</p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setLiveTimeForm({...liveTimeForm, mode: 'manual'})}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                      liveTimeForm.mode === 'manual'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    ✎ Manual Control
                  </button>
                  <button
                    type="button"
                    onClick={() => setLiveTimeForm({...liveTimeForm, mode: 'auto'})}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                      liveTimeForm.mode === 'auto'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    ⏱️ Auto-Detect
                  </button>
                </div>
              </div>

              {/* Manual Mode Controls */}
              {liveTimeForm.mode === 'manual' && (
                <div className="space-y-4 mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Match Phase</label>
                      <select
                        value={liveTimeForm.matchPhase}
                        onChange={(e) => setLiveTimeForm({...liveTimeForm, matchPhase: e.target.value})}
                        className="w-full input-field"
                      >
                        <option value="1st Half">1st Half</option>
                        <option value="Break">Break</option>
                        <option value="2nd Half">2nd Half</option>
                        <option value="Extra Time">Extra Time</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Current Minute</label>
                      <input
                        type="number"
                        min="0"
                        max="120"
                        value={liveTimeForm.currentMinute}
                        onChange={(e) => setLiveTimeForm({...liveTimeForm, currentMinute: e.target.value})}
                        className="w-full input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Stoppage Time (minutes)</label>
                      <input
                        type="number"
                        min="0"
                        value={liveTimeForm.stoppageTime}
                        onChange={(e) => setLiveTimeForm({...liveTimeForm, stoppageTime: e.target.value})}
                        className="w-full input-field"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Auto Mode Controls */}
              {liveTimeForm.mode === 'auto' && (
                <div className="space-y-4 mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-gray-700 mb-3">Auto mode will automatically calculate elapsed time from match kickoff</p>
                  
                  <button
                    type="button"
                    onClick={() => handleStartAutoTime(fixtures.find(f => f._id === expandedLiveTime))}
                    disabled={loading}
                    className="w-full px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {loading ? <FiLoader className="w-4 h-4 animate-spin" /> : '▶'}
                    Start Auto-Detection
                  </button>

                  {liveTimeForm.currentMinute > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => handleTogglePause(fixtures.find(f => f._id === expandedLiveTime))}
                        disabled={loading}
                        className={`w-full px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                          liveTimeForm.isPaused
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-orange-600 text-white hover:bg-orange-700'
                        } disabled:bg-gray-400`}
                      >
                        {loading ? <FiLoader className="w-4 h-4 animate-spin" /> : (liveTimeForm.isPaused ? '▶' : '⏸')}
                        {liveTimeForm.isPaused ? 'Resume' : 'Pause'}
                      </button>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Stoppage Time (minutes)</label>
                        <input
                          type="number"
                          min="0"
                          value={liveTimeForm.stoppageTime}
                          onChange={(e) => setLiveTimeForm({...liveTimeForm, stoppageTime: e.target.value})}
                          className="w-full input-field"
                        />
                      </div>

                      {liveTimeForm.matchPhase === '1st Half' && liveTimeForm.currentMinute >= 45 && (
                        <button
                          type="button"
                          onClick={() => handleStartSecondHalf(fixtures.find(f => f._id === expandedLiveTime))}
                          disabled={loading}
                          className="w-full mt-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors"
                        >
                          Start 2nd Half
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={() => setExpandedLiveTime(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleSaveLiveTime(fixtures.find(f => f._id === expandedLiveTime))}
                  disabled={loading || !liveTimeForm.mode}
                  className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  {loading ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiSave className="w-4 h-4" />}
                  Save Time
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'standings' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">🏆 League Standings</h3>
            <p className="text-sm text-gray-600">Current season standings based on completed matches</p>
          </div>

          {players && players.length > 0 && (
            <div className="p-4 border-b border-gray-100 bg-gray-50">
              <h4 className="text-sm font-semibold text-gray-800 mb-2">Top Scorers</h4>
              <div className="flex gap-4 overflow-x-auto">
                {players
                  .filter(pl => pl.goals && pl.goals > 0)
                  .sort((a, b) => (b.goals || 0) - (a.goals || 0))
                  .slice(0, 10)
                  .map((pl, i) => (
                    <div key={`${pl.teamName}-${pl.playerName}`} className="px-3 py-2 bg-white border rounded-md shadow-sm">
                      <div className="text-sm font-medium text-gray-900">{pl.playerName}</div>
                      <div className="text-xs text-gray-600">{pl.teamName}</div>
                      <div className="text-sm font-bold text-primary-600">{pl.goals} goals</div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {standings.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500 text-lg">No standings data yet. Complete some matches to see the league table.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-300">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-900">Pos</th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-900">Team</th>
                    <th className="px-4 py-3 text-center text-sm font-bold text-gray-900">P</th>
                    <th className="px-4 py-3 text-center text-sm font-bold text-gray-900">W</th>
                    <th className="px-4 py-3 text-center text-sm font-bold text-gray-900">D</th>
                    <th className="px-4 py-3 text-center text-sm font-bold text-gray-900">L</th>
                    <th className="px-4 py-3 text-center text-sm font-bold text-gray-900">GF</th>
                    <th className="px-4 py-3 text-center text-sm font-bold text-gray-900">GA</th>
                    <th className="px-4 py-3 text-center text-sm font-bold text-gray-900">GD</th>
                    <th className="px-4 py-3 text-center text-sm font-bold text-white bg-primary-600">Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.map((standing, idx) => (
                    <tr
                      key={standing._id}
                      className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${
                        idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                      }`}
                    >
                      {/* Position */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center">
                          <span
                            className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${
                              idx === 0
                                ? 'bg-yellow-400 text-yellow-900'
                                : idx === 1
                                ? 'bg-gray-400 text-white'
                                : idx === 2
                                ? 'bg-orange-400 text-white'
                                : 'bg-gray-200 text-gray-700'
                            }`}
                          >
                            {standing.position}
                          </span>
                        </div>
                      </td>

                      {/* Team Name */}
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-900">{standing.teamName}</p>
                      </td>

                      {/* Played */}
                      <td className="px-4 py-3 text-center text-sm font-medium text-gray-700">{standing.played}</td>

                      {/* Wins */}
                      <td className="px-4 py-3 text-center text-sm font-medium text-green-700 bg-green-50">
                        {standing.wins}
                      </td>

                      {/* Draws */}
                      <td className="px-4 py-3 text-center text-sm font-medium text-blue-700 bg-blue-50">
                        {standing.draws}
                      </td>

                      {/* Losses */}
                      <td className="px-4 py-3 text-center text-sm font-medium text-red-700 bg-red-50">
                        {standing.losses}
                      </td>

                      {/* Goals For */}
                      <td className="px-4 py-3 text-center text-sm font-semibold text-gray-900">{standing.goalsFor}</td>

                      {/* Goals Against */}
                      <td className="px-4 py-3 text-center text-sm font-semibold text-gray-900">
                        {standing.goalsAgainst}
                      </td>

                      {/* Goal Difference */}
                      <td className="px-4 py-3 text-center text-sm font-semibold text-gray-900">
                        <span
                          className={`inline-block px-2 py-1 rounded ${
                            standing.goalDifference > 0
                              ? 'bg-green-100 text-green-800'
                              : standing.goalDifference < 0
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {standing.goalDifference > 0 ? '+' : ''}
                          {standing.goalDifference}
                        </span>
                      </td>

                      {/* Points */}
                      <td className="px-4 py-3 text-center text-sm font-bold text-white bg-primary-600">
                        {standing.points}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
